import { Flex, Text, Tooltip, useDisclosure } from "@chakra-ui/react";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { BaseTableFilters } from "@/components/BaseTableFilters/BaseTableFilters";
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout/Layout";
import { RootState, useAppDispatch, useAppSelector } from "@/store";
import { useEffect, useMemo, useState } from "react";
import { formatDate } from "@/helpers/formatDate";
import { Vacancy, VacanciesFilter } from "@/model/vacancy";
import { Customer } from "@/model/customers";
import { Project } from "@/model/projects";
import { Recruiter } from "@/model/recruiters";
import api from "@/api";
import { fetchAllVacancies } from "@/store/slices/vacancy";
import { Pagination } from "@/components/Pagination/Pagination";
import { VacanciesPopupFilter } from "./components/VacanciesPopupFilter";
import { defaultVacanciesFilterFiedls } from "./_helpers";
import { useSorting } from "@/hooks/useSorting";
import AssignedItemsCell from "@/components/AssignedItemsCell/AssignedItemsCell";
import { toUnix } from "@/utils/date";
import { getOffset } from "@/utils/pagination";
import { useSessionStorageFilters } from "@/hooks/useSessionStorageFilters";

const RecruitersCell = (info: CellContext<Vacancy, unknown>) => {
  const recruiters = (info?.row.original.recruiters || []) as Vacancy["recruiters"];
  if (!recruiters.length) {
    return <Text>Рекрутеры не назначены</Text>;
  }
  return <AssignedItemsCell items={recruiters.map((r) => r.username)} maxVisible={1} ariaLabel="Назначенные рекрутеры" />;
};

export const VacancyPage = () => {
  const dispatch = useAppDispatch();
  const { vacancies, isLoading } = useAppSelector((state: RootState) => state.vacancy);
  const { isOpen: isFiltersOpen, onOpen: onFiltersOpen, onClose: onFiltersClose } = useDisclosure();
  const { sortColumn, sortDirection, handleSort } = useSorting("vacancy.created_at");

  const columnHelper = createColumnHelper<Vacancy>();

  const columns = [
    columnHelper.accessor("customer.name", {
      id: "customer.name",
      header: "Заказчик",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="120px" fontWeight={600} color="#3b515e" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 120,
      minSize: 80,
    }),
    columnHelper.accessor("name", {
      id: "name",
      header: "Название",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="160px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 160,
      minSize: 120,
    }),
    columnHelper.accessor("project.name", {
      id: "project.name",
      header: "Проект",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="120px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 120,
      minSize: 80,
    }),
    columnHelper.accessor("status.name", {
      id: "status.name",
      header: "Cтатус вакансии",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="120px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 120,
      minSize: 80,
    }),
    columnHelper.accessor("recruiters", {
      id: "recruiters",
      header: "Назначенные рекрутеры",
      cell: RecruitersCell,
    }),
    columnHelper.accessor("candidates_count", {
      id: "candidates_count",
      header: "Кандидаты",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="80px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 80,
      minSize: 40,
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: "Дата создания",
      cell: (info) => (
        <Tooltip label={formatDate(info.getValue() || "")} hasArrow>
          <Text noOfLines={1} maxW="80px" fontSize={14}>
            {formatDate(info.getValue() || "")}
          </Text>
        </Tooltip>
      ),
      enableSorting: true,
      size: 80,
      minSize: 40,
    }),
  ];

  const [totalItems, setTotalItems] = useState(0);
  const [searchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 20;

  // Используем универсальный хук для работы с фильтрами
  const { formik, handleSearch, handleApplyFilters, handleResetFilters } = useSessionStorageFilters<VacanciesFilter>({
    storageKey: "vacanciesPageFilters",
    defaultValues: defaultVacanciesFilterFiedls,
  });
  const [customers, setCustomers] = useState<readonly Customer[]>([]);
  const [allProjects, setAllProjects] = useState<readonly Project[]>([]);
  const [recruiters, setRecruiters] = useState<readonly Recruiter[]>([]);

  const navigate = useNavigate();

  // Prefetch shared lists for popup filters
  useEffect(() => {
    const fetchLists = async () => {
      try {
        const [customersRes, projectsRes, recruitersRes] = await Promise.all([
          api.customers.customers({ params: { filters: { limit: "1000", offset: "0" } } }),
          api.projects.projects({ params: { filters: { limit: "1000", offset: "0" }, expand: ["customer"] } }),
          api.recruiters.recruiters({}),
        ]);
        setCustomers(customersRes?.data?.items || []);
        setAllProjects(projectsRes?.data?.items || []);
        setRecruiters(recruitersRes?.data || []);
      } catch {
        setCustomers([]);
        setAllProjects([]);
        setRecruiters([]);
      }
    };
    fetchLists();
  }, []);

  useEffect(() => {
    const offset = getOffset(page, limit);

    const { created_at_from, created_at_to, ...restFilters } = formik.values;
    const computedFilters = {
      ...restFilters,
      created_at_from: toUnix(created_at_from),
      created_at_to: toUnix(created_at_to),
      sort_column: sortColumn || undefined,
      sort_direction: sortDirection || undefined,
      limit,
      offset,
    };

    dispatch(
      fetchAllVacancies({
        params: {
          filters: computedFilters,
        },
      }),
    ).then((res: any) => {
      setTotalItems(res.payload.count);
    });
  }, [dispatch, page, limit, formik.values, sortColumn, sortDirection]);

  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (formik.values.customer_id) count += 1;
    if (formik.values.project_id) count += 1;
    if (formik.values.status) count += 1;
    if (formik.values.recruiter_id) count += 1;
    if (formik.values.created_at_from) count += 1;
    if (formik.values.created_at_to) count += 1;
    return count;
  }, [
    formik.values.customer_id,
    formik.values.project_id,
    formik.values.status,
    formik.values.recruiter_id,
    formik.values.created_at_from,
    formik.values.created_at_to,
  ]);

  return (
    <Layout>
      <Flex flexDirection="column" maxH="90vh" overflowY="auto" gap="24px">
        <Text fontSize={27} fontWeight={500}>
          Вакансии
        </Text>
        <BaseTableFilters
          handleSearch={handleSearch}
          placeHolderInput="Поиск по вакансиям"
          searchValue={formik.values.search || ""}
          isShowBtnPopupFilters
          selectedFiltersCount={appliedFiltersCount}
          onClickBtnPopupFilters={onFiltersOpen}
          isOpenPopupFilters={isFiltersOpen}
          onClosePopupFilters={onFiltersClose}
          popupFilters={
            <VacanciesPopupFilter
              values={formik.values}
              onApply={(v) => {
                handleApplyFilters(v);
                onFiltersClose();
              }}
              onReset={() => {
                handleResetFilters();
                onFiltersClose();
              }}
              customers={customers}
              allProjects={allProjects}
              recruiters={recruiters}
            />
          }
          isLoading={isLoading}
          isShowCreateButton={false}
        />

        <BaseTable
          list={vacancies?.items || []}
          columns={columns}
          sortHandler={({ id, direction }) => handleSort({ id, direction: direction ?? null })}
          sortState={{
            column: sortColumn || "",
            direction: sortDirection,
          }}
          isLoading={isLoading}
        />

        <Pagination
          totalItems={totalItems}
          isLoading={isLoading}
          onPageChange={(offset, limit) => {
            const params = new URLSearchParams(searchParams);
            params.set("page", String(offset / limit + 1));
            params.set("limit", String(limit));
            navigate(`?${params.toString()}`);
          }}
        />
      </Flex>
    </Layout>
  );
};
